import { Button, Stack, Typography, useTheme } from '@mui/material';
import { scaledFontSize } from './createWidgetTheme';
import { useWidgetBrandColors } from './useWidgetBrandColors';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';
import { visuallyHiddenSx } from './visuallyHiddenSx';

interface QuickStartButton {
  label: string;
  message: string;
}

interface FloatingButtonHoverChipsProps {
  buttons: QuickStartButton[];
  chipsVisible: boolean;
  onChipClick: (label: string, message: string) => void;
}

const QUICK_ACTIONS_LABEL_ID = 'launcher-quick-actions-label';

export function FloatingButtonHoverChips({
  buttons,
  chipsVisible,
  onChipClick,
}: FloatingButtonHoverChipsProps) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const { primaryOnPaper } = useWidgetBrandColors();

  return (
    <Stack
      role="group"
      aria-labelledby={QUICK_ACTIONS_LABEL_ID}
      spacing={1}
      sx={{
        alignItems: 'flex-end',
        mb: chipsVisible ? 1.5 : 0,
        opacity: chipsVisible ? 1 : 0,
        transform: chipsVisible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 200ms ease, transform 200ms ease',
        pointerEvents: chipsVisible ? 'auto' : 'none',
        maxHeight: chipsVisible ? 400 : 0,
        overflow: 'hidden',
      }}
    >
      <Typography
        component="span"
        id={QUICK_ACTIONS_LABEL_ID}
        lang={locale}
        sx={visuallyHiddenSx}
      >
        {t('launcher.quickActions')}
      </Typography>
      {buttons.map((button, index) => (
        <Button
          key={`${button.label}-${index}`}
          variant="outlined"
          aria-label={button.label}
          tabIndex={chipsVisible ? 0 : -1}
          onClick={() => onChipClick(button.label, button.message)}
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: '9999px',
            color: primaryOnPaper,
            borderColor: theme.palette.grey[300],
            px: 2,
            py: 0.75,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: scaledFontSize(theme, 13),
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            minHeight: 44,
            '&:hover': {
              bgcolor: theme.palette.background.paper,
              borderColor: primaryOnPaper,
            },
          }}
        >
          {button.label}
        </Button>
      ))}
    </Stack>
  );
}
